import { getApiHostname } from '../../api';
import { StringSchema } from 'yup';
import * as Yup from 'yup';
import axios from 'axios';
import memo from 'lodash.memoize';

export interface ValidateEmailResponse {
  data?: {
    email: string;
    is_valid: boolean | null;
  };
  errors?: {
    message: string;
    email: string;
    is_valid: false;
  }[];
}

const EMAIL_VALIDATION_API_ENDPOINT = `${getApiHostname()}/recommends/app/email-validation`;

async function checkEmailValue(
  email: string
): Promise<ValidateEmailResponse | undefined> {
  const response = await axios.post<ValidateEmailResponse>(
    EMAIL_VALIDATION_API_ENDPOINT,
    {
      email,
      premium_validation: false,
    },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      // Prevent 422 status codes from causing axios to throw an error
      validateStatus: (statusCode: number) =>
        statusCode === 422 || statusCode < 300,
    }
  );
  if (response.data) {
    return response.data;
  }
  return undefined;
}

const memoizedCheckEmailValue = memo((email: string) => checkEmailValue(email));

Yup.addMethod<StringSchema>(
  Yup.string,
  'emailValidator',
  function validateEmail(message: string) {
    return this.test(
      'test-email',
      message,
      async function validateEmailValue(email) {
        const { path, createError } = this;

        try {
          await Yup.string().email(message).validate(email);
        } catch (error) {
          return createError({ path, message: error.message });
        }

        if (typeof email === 'undefined' || email === '') {
          return true;
        }

        try {
          const response = await memoizedCheckEmailValue(email);
          const isValid = response?.data?.is_valid;

          if (!isValid) {
            return createError({ path, message });
          }

          return true;
        } catch (err) {
          // 1. Handles the endpoint failing to respond (ie. if the endpoint is down).
          // 2. Prevents the user from being stuck in the lead form with an invalid email error, even if their email is valid.
          return true;
        }
      }
    );
  }
);

declare module 'yup' {
  interface StringSchema {
    emailValidator(message: string): StringSchema;
  }
}
